import React, { memo, useCallback, useMemo } from 'react';
import { isEmpty } from 'ramda';
import arePropsEqual from 'react-fast-compare';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Heading from '../Heading/Heading';
import GameCard from '../GameCard/GameCard';
import CardsLine from '../CardsLine/CardsLine';
import { useIsSSCOMBrand } from '../../hooks/useIsSSCOMBrand';
import { useColumnCount } from '../CardsLine/hooks';
import { useElementRef } from '../../hooks/useElementRef';
import { createMockEntitiesArray } from '../../utils';
import { convertGameDataToMatrix } from './utils';
import { ANALYTICS_SCHEMA_URLS } from '../../constants/analytics';
import styles from './GameCardsLine.module.css';
import cardsLineStyles from '../CardsLine/CardsLine.module.css';

export const COUNT_SKELETON_CARDS = 8;

const getPositionIndex = (matrix, lineIndex, gameIndex) => {
  return gameIndex * matrix.length + lineIndex;
};

const GameCardsLine = ({
  title,
  link,
  items,
  lines,
  scrollable,
  isLoading,
  className,
  additionalLayerData,
  eventAction,
  eventActionPlay,
  eventActionPlayDemo,
  eventActionFavorite,
  sectionIndex,
  analyticsData,
  ...props
}) => {
  const [componentElement, componentRef] = useElementRef();
  const count = useColumnCount(componentElement);

  const collection = isLoading
    ? createMockEntitiesArray(COUNT_SKELETON_CARDS * lines)
    : items;

  const matrix = useMemo(() => {
    return convertGameDataToMatrix(collection, count, lines, scrollable);
  }, [collection, lines, scrollable, count]);

  const isSCCOM = useIsSSCOMBrand();

  const renderCard = useCallback(
    (item, index) => {
      return (
        <GameCard
          key={item.id}
          sectionIndex={sectionIndex}
          index={index}
          eventActionPlay={eventActionPlay}
          eventActionPlayDemo={eventActionPlayDemo}
          additionalLayerData={additionalLayerData}
          eventActionFavorite={eventActionFavorite}
          game={item}
          isLoading={isLoading}
          analyticsData={{ ...analyticsData, section_index: sectionIndex }}
        />
      );
    },
    [
      additionalLayerData,
      eventActionPlay,
      eventActionPlayDemo,
      eventActionFavorite,
      isLoading,
      sectionIndex,
      analyticsData,
    ]
  );
  return isEmpty(collection) ? null : (
    <div
      className={classNames(
        styles.component_ig,
        cardsLineStyles.component_ig,
        className
      )}
      ref={componentRef}
      {...props}
    >
      <Heading
        link={link}
        isLoading={isLoading}
        additionalLayerData={additionalLayerData}
        eventAction={eventAction}
        analyticsData={{
          ...analyticsData,
          section_index: sectionIndex,
          schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
        }}
      >
        {title}
      </Heading>
      {matrix.map((collection, lineIndex) => (
        <CardsLine
          scrollable={!isLoading && scrollable}
          key={lineIndex}
          items={collection}
          render={(game, gameIndex) =>
            renderCard(game, getPositionIndex(matrix, lineIndex, gameIndex))
          }
          className={classNames(styles.line_ig, !isSCCOM && styles.titled_ig)}
        />
      ))}
    </div>
  );
};

GameCardsLine.propTypes = {
  link: PropTypes.string,
  items: PropTypes.array,
  lines: PropTypes.number,
  scrollable: PropTypes.bool,
  isLoading: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  additionalLayerData: PropTypes.shape({}),
  eventAction: PropTypes.string,
  eventActionPlay: PropTypes.string,
  eventActionPlayDemo: PropTypes.string,
  sectionIndex: PropTypes.number,
  analyticsData: PropTypes.shape({
    screen_name: PropTypes.string,
    screen_area: PropTypes.string,
    screen_area_detail: PropTypes.string,
    event_detail: PropTypes.string,
  }),
};

GameCardsLine.defaultProps = {
  link: '',
  title: '',
  items: [],
  lines: 1,
  scrollable: true,
  isLoading: false,
  analyticsData: {},
};

export default memo(GameCardsLine, arePropsEqual);
